import { resolvePortValue, type NodeImplementation } from '@spotar/runtime';
import TrayChoice from '../tray-choice';
import { parseBoolean } from '../helper/parse-helper';
import { hideTrayHint, showTrayHint, type TrayHintStyle } from '../tray-hint';

type TrayHighlightData = {
  element?: unknown;
  option?: string;
  style?: string;
  active?: unknown;
};

function readTrayChoice(element: unknown): TrayChoice | null {
  const resolved = resolvePortValue(element);
  return resolved instanceof TrayChoice ? resolved : null;
}

function resolveHintTarget(trayChoice: TrayChoice, optionId: string): HTMLElement | null {
  if (!optionId) return trayChoice.getTrackElement();
  return trayChoice.getOptionElement(optionId);
}

/**
 * Every element a hint can sit on. Deactivating without a specific option clears
 * them all, so one "off" node reliably removes whatever was set before.
 */
function collectHintedElements(trayChoice: TrayChoice): HTMLElement[] {
  const track = trayChoice.getTrackElement();
  const options = trayChoice.getOptionElements();
  return track ? [track, ...options] : options;
}

function toHintStyle(style: string | undefined): TrayHintStyle {
  return style === 'glowBackdrop' ? 'glowBackdrop' : 'pulseBorder';
}

function hideHints(trayChoice: TrayChoice, optionId: string) {
  if (!optionId) {
    collectHintedElements(trayChoice).forEach(hideTrayHint);
    return;
  }
  const target = trayChoice.getOptionElement(optionId);
  if (target) hideTrayHint(target);
}

export const runTrayHighlightImplementation: NodeImplementation = (data) => {
  const { element, option, style, active } = data as TrayHighlightData;

  const trayChoice = readTrayChoice(element);
  if (!trayChoice) throw new Error('no tray element is wired to this node');

  const optionId = (option ?? '').trim();
  if (parseBoolean(active, true) === false) {
    hideHints(trayChoice, optionId);
    return;
  }

  const target = resolveHintTarget(trayChoice, optionId);
  if (!target) throw new Error(`the tray element has no option "${optionId}"`);
  showTrayHint(target, toHintStyle(style));
};
